import { Organization } from "./Organization";
import { Color, TransferMethod } from "./LogoOptions";
import { Approval, ApprovalGroup, ApprovalStatus } from "./Approval";
import { Price } from "./Price";
import { NotImplemented } from "../../../presentation/shared/functions/not-implemented.function";
import { AdditionalFee } from "./Order";
import { NumberLogoStorage } from "../../../data/models/responses/LogoDto";

export enum ApprovalType {
  Customer = 0,
  External = 1,
}

export interface BaseLogo {
  inStorage: number;
  numberLogosInStorage: NumberLogoStorage[];
  title: string;
  displayImage: ImageRef;
  heightMm: number;
  widthMm: number;
  designerWidthMm: number;
  transferMethod: TransferMethod;
  customer: Organization;
  customerId: number;
  type: string;
  id: number;
  createdAtUtc: Date;
  updateAtUtc?: Date;
  CreationTime?: Date;
  createdBy: string;
  isDeleted: boolean;
  deletedAtUtc?: Date;
  product?: ProductRef;
  productSku?: string;
  logoType: LogoType;
  approval?: ApprovalGroup;
  designCount: number;
  colors: string[];
  colorDetails: Color[];
  approvalType?: ApprovalType;
  mainOrganizationId?: number;
  Classification?: number | string;
  stitches?: number;
  serial?: string;
  isForAllCustomers?: boolean;
  // True while a recovered logo waits for its print image to be generated by
  // the background worker (displayImage URLs are null until then). Flows from
  // LogoDto through the mapper spread, like `serial`.
  awaitingImage?: boolean;
}

export function GetOverallApprovalStatus(
  approval: ApprovalGroup
): ApprovalStatus {
  // Defensive: callers historically pass `logo.approval!`, but foreign-org
  // rows (admin/act-as lists) can genuinely lack an approval group — treat
  // that as Pending instead of throwing during render.
  if (!approval) {
    return ApprovalStatus.Pending;
  }

  function isState(approval: Approval, state: ApprovalStatus): boolean {
    return approval.status === state;
  }

  const rejected = ApprovalStatus.Rejected;
  const approved = ApprovalStatus.Approved;

  // Check for rejections first - any rejection means overall rejection
  if (
    (approval.vendor ? isState(approval.vendor, rejected) : false) ||
    (approval.customer ? isState(approval.customer, rejected) : false) ||
    (approval.external ? isState(approval.external!, rejected) : false)
  ) {
    return ApprovalStatus.Rejected;
  }

  // Vendor approval is sufficient - if Vendor has approved, design is approved
  // regardless of End Customer status (but Customer must also be approved if exists)
  if (approval.vendor && isState(approval.vendor, approved)) {
    // If there's a customer approval requirement, it must also be approved
    if (approval.customer && !isState(approval.customer, approved)) {
      return ApprovalStatus.Pending;
    }
    // Vendor approval overrides pending End Customer approval
    return ApprovalStatus.Approved;
  }

  // Original logic for cases without vendor approval
  if (
    (approval.vendor ? isState(approval.vendor, approved) : true) &&
    (approval.customer ? isState(approval.customer, approved) : true) &&
    (approval.external ? isState(approval.external!, approved) : true)
  ) {
    return ApprovalStatus.Approved;
  }

  return ApprovalStatus.Pending;
}

/**
 * Determines if approval buttons should be shown based on the specific approval type
 * and the current approval state for that type.
 */
export function shouldShowApprovalButtons(
  approval: ApprovalGroup | undefined | null,
  approvalType: ApprovalType
): boolean {
  if (!approval) {
    return false;
  }

  // Check the specific approval status for the given type
  if (approvalType === ApprovalType.Customer) {
    // Show buttons if customer approval is pending
    return approval.customer?.status === ApprovalStatus.Pending;
  } else if (approvalType === ApprovalType.External) {
    // Show buttons if external approval is pending
    return approval.external?.status === ApprovalStatus.Pending;
  }

  return false;
}

/**
 * Checks if the current user has pending approval status based on their role
 * @param approval The approval group to check
 * @param userRole The current user's role (0=EndUser/Customer, 1=Vendor, 2=Admin)
 * @returns true if the user has pending approval status, false otherwise
 */
export function hasUserPendingApprovalStatus(
  approval: ApprovalGroup | undefined | null,
  userRole: number
): boolean {
  if (!approval || userRole === undefined || userRole === null) {
    return false;
  }

  // Role enum values: Admin = 2, Vendor = 1, EndUser = 0
  switch (userRole) {
    case 1: // Vendor
      // Vendor has pending status if vendor approval is pending
      return approval.vendor?.status === ApprovalStatus.Pending;
    case 0: // EndUser (Customer)
      // Customer has pending status if customer approval is pending
      return approval.customer?.status === ApprovalStatus.Pending;
    case 2: // Admin
      // Admin doesn't have pending approval status in the current flow
      return false;
    default:
      return false;
  }
}

export function getApprovalBorderColor(
  approval: ApprovalGroup | undefined | null,
  approvalType: ApprovalType | undefined
): string {
  if (!approval || approvalType === undefined) {
    return "border border-borderGray";
  }

  // Get the specific approval status for the given type
  let status: ApprovalStatus | undefined;
  switch (approvalType) {
    case ApprovalType.Customer:
      status = approval.customer?.status;
      break;
    case ApprovalType.External:
      status = approval.external?.status;
      break;
    default:
      status = undefined;
  }

  // Determine border color based on the specific approval status
  if (status === ApprovalStatus.Pending) {
    return "border-2 border-amber-100";
  } else if (status === ApprovalStatus.Rejected) {
    return "border-2 border-rose-100";
  }

  // Default border for approved or undefined status
  return "border border-borderGray";
}

export enum LogoType {
  Unknown = "Unknown",
  TextLogo = "TextLogo",
  NameLogo = "NameLogo",
  NumberLogo = "NumberLogo",
  ImageLogo = "ImageLogo",
  SymbolLogo = "SymbolLogo",
}

export interface TextLogo extends BaseLogo {
  value: string;
  font: string;
}

export interface NameLogo extends BaseLogo {
  font: string;
}

export interface NumberLogo extends BaseLogo {
  font: string;
}

export interface ImageLogo extends BaseLogo {
  isDigital: boolean;
  isChangeColor: boolean;
  status: LogoEditStatus;
  colorChange?: TemporaryColorChange;
  customerId: number;
  originalFileName: string;
}

export interface SymbolLogo extends BaseLogo {
  symbolKey: string;
  color: string;
}

export interface TemporaryColorChange {
  colorChangeCode: string;
  startedAtUtc: Date;
  ttlSec: number;
}

export enum LogoEditStatus {
  Unknown = "unknown",
  Draft = "draft",
  Rejected = "rejected",
  Validating = "validating",
  Done = "done",
}

export interface ImageRef {
  digitUrl: string;
  lowResUrl: string;
  highResUrl: string;
  originalUrl: string;
  trueColorsUrl?: string;
  // ADDITIVE: true when the server sent no highResUrl and the mapper fell
  // back to lowResUrl (the cold "sharp render not done yet" state). The
  // fallback itself is kept for all existing consumers; this field lets new
  // consumers see through it. Optional: cart snapshots persisted before this
  // field existed simply carry undefined.
  highResPending?: boolean;
}

export interface ProductRef {
  sku: string;
  prices: ProductPrice[];
  additionalFees: AdditionalFee[];
}

export interface ProductPrice {
  type: string;
  currency?: string;
  startDate?: string;
  endDate?: string;
  minQuantity: number;
  adjustedPrice: number;
  adjustedUnitPrice: number;
  adjustedCustomerPrice?: number;
}

// Global logos (IsForAllCustomers=true) carry no customer/vendor and use
// MainOrganizationId=0 as the documented sentinel. WithLogoAccess short-circuits
// on IsForAllCustomers and the repository's GetByIdAsync skips the org-filter
// when orgId<=0, so passing 0 is the supported "no scope" path.
export function getLogoOrgId(logo: BaseLogo): number {
  return logo.customer?.id ?? logo.mainOrganizationId ?? 0;
}

export function isDigital(logo: BaseLogo): boolean {
  // transferMethod?.: rows from foreign orgs (admin/act-as lists) can arrive
  // without a transfer method (mapLogo turns a null DTO field into {}).
  if (
    logo.transferMethod?.method1?.toLowerCase().includes("digital") ||
    logo.transferMethod?.method2?.toLowerCase().includes("digital")
  ) {
    return true;
  }
  return false;
}

export function isChangeColor(logo: BaseLogo): boolean {
  if (logo.logoType === LogoType.ImageLogo) {
    return (logo as ImageLogo).isChangeColor;
  }
  return false;
}

export type LogoVariant = "name" | "number" | "static";
export type PlaceholderLogoVariant = "name" | "number";
export type DeliveryVariant = "Application" | "Delivery" | "Storage";

export function getLogoType(logo: BaseLogo): LogoVariant {
  switch (logo.logoType) {
    case LogoType.Unknown:
      console.warn("Unknown logo type detected");
      NotImplemented("Unknown logo type detected");
      return "static";
    case LogoType.TextLogo:
      return "static";
    case LogoType.NameLogo:
      return "name";
    case LogoType.NumberLogo:
      return "number";
    case LogoType.ImageLogo:
      return "static";
    case LogoType.SymbolLogo:
      return "static";
  }
}

export function isPlaceholder(logo: BaseLogo): boolean {
  const type = getLogoType(logo);
  return type === "name" || type === "number";
}

export function getTypeText(logo: BaseLogo): string {
  // transferMethod?.: same foreign-org guard as isDigital above.
  const type = logo.transferMethod?.type?.toLowerCase() ?? "";

  if (type === "direk tryk" || type === "screen print") {
    return "Screen Print";
  }
  if (type === "broderi" || type === "embroidery") {
    return "Embroidery";
  }
  if (type === "transfer") {
    return "Transfer";
  }
  return logo.transferMethod?.type ?? "";
}

export type LcaType = "Broderi" | "Direkte Tryk" | "Transfer" | "Digital Transfer";

export function getLcaType(logo: BaseLogo): LcaType | null {
  const type = logo.transferMethod?.type?.toLowerCase();
  if (!type) return null;
  if (type === "broderi" || type === "embroidery") return "Broderi";
  if (type === "direk tryk" || type === "direkte tryk" || type === "screen print") return "Direkte Tryk";
  if (type === "transfer") return isDigital(logo) ? "Digital Transfer" : "Transfer";
  return null;
}

export type ColorKey = "NEON" | "BLACK" | "WHITE" | "METAL" | "OTHER";

export function getColorKey(color: Color): ColorKey {
  if (color.isNeon) return "NEON";
  if (color.isMetallic) return "METAL";
  const id = color.id?.toLowerCase();
  if (id === "hvid") return "WHITE";
  if (id === "sort") return "BLACK";
  return "OTHER";
}

export function statusToString(status: LogoEditStatus): string {
  switch (status) {
    case LogoEditStatus.Unknown:
      return "Unknown";
    case LogoEditStatus.Draft:
      return "Draft";
    case LogoEditStatus.Rejected:
      return "Rejected";
    case LogoEditStatus.Validating:
      return "Validating";
    case LogoEditStatus.Done:
      return "Done";
    default:
      console.warn("unknown status", status);
      return "Unknown";
  }
}

export function getLogoColors(
  logo: BaseLogo,
  colorOptions: Color[],
  embroideryColors: Color[] = []
): Color[] {
  // Use colorDetails from database if available (contains correct isBackup values)
  if (logo.colorDetails && logo.colorDetails.length > 0) {
    return logo.colorDetails;
  }

  // Fallback to dictionary lookup for logos without colorDetails.
  // (logo.colors ?? []): foreign-org rows can carry neither colorDetails nor
  // colors — rendering must degrade to "no colors", not throw.
  const colors =
    (logo.type === "BRODERI" ? embroideryColors : colorOptions) ?? [];
  return (logo.colors ?? [])
    .map((id) => colors.find((c) => id === c.id))
    .filter((c) => !!c) as Color[];
}

export function getLogoState(logo: BaseLogo): LogoState {
  const imageLogo = logo as ImageLogo;

  if (imageLogo.status === LogoEditStatus.Draft) {
    return LogoState.Draft;
  }

  if (imageLogo.status === LogoEditStatus.Rejected) {
    return LogoState.Rejected;
  }

  if (imageLogo.status === LogoEditStatus.Done) {
    return LogoState.Approved;
  }

  if (imageLogo.approval === undefined) {
    return LogoState.Undefined;
  }

  switch (GetOverallApprovalStatus(imageLogo.approval!)) {
    case ApprovalStatus.Rejected:
      return LogoState.Rejected;
    case ApprovalStatus.Approved:
      return LogoState.Approved;
    case ApprovalStatus.Pending:
      return LogoState.Pending;
  }
}

export enum LogoState {
  Draft = "Draft",
  Rejected = "Rejected",
  Pending = "Pending",
  Approved = "Approved",
  Undefined = "Undefined",
}

export function getTransferDisplayName(logo: BaseLogo) {
  return (
    logo.transferMethod.displayName ??
    (logo.transferMethod?.method1 ?? "N/A") +
      (logo.transferMethod.method2 ? `, ${logo.transferMethod.method2}` : "")
  );
}

export function getProductPrice(
  productPrices: ProductPrice[],
  quantity: number
): ProductPrice | undefined {
  let highlightedMinQuantity: number | undefined =
    productPrices.sort((x) => x.minQuantity).at(0)?.minQuantity ?? undefined;

  productPrices.forEach((p) => {
    if (highlightedMinQuantity === undefined) {
      highlightedMinQuantity = p.minQuantity;
    }
    if (quantity >= p.minQuantity) {
      if (highlightedMinQuantity == undefined) {
        highlightedMinQuantity = p.minQuantity;
      }
      if (p.minQuantity > highlightedMinQuantity) {
        highlightedMinQuantity = p.minQuantity;
      }
    }
  });

  return productPrices.find((x) => x.minQuantity == highlightedMinQuantity);
}

export function GetProductMinQuantity(
  productPrices: ProductPrice[],
  quantity: number
): number | undefined {
  let highlightedMinQuantity: number | undefined = undefined;
  productPrices.forEach((p) => {
    if (quantity >= p.minQuantity) {
      if (highlightedMinQuantity == undefined) {
        highlightedMinQuantity = p.minQuantity;
      }
      if (p.minQuantity > highlightedMinQuantity) {
        highlightedMinQuantity = p.minQuantity;
      }
    }
  });

  return highlightedMinQuantity;
}

export function getDesignProductPrice(
  productPrices: Price[],
  quantity: number
): Price | undefined {
  let highlightedMinQuantity: number | undefined = undefined;
  productPrices.forEach((p) => {
    if (quantity >= p.minQuantity) {
      if (highlightedMinQuantity == undefined) {
        highlightedMinQuantity = p.minQuantity;
      }
      if (p.minQuantity > highlightedMinQuantity) {
        highlightedMinQuantity = p.minQuantity;
      }
    }
  });

  return productPrices.find((x) => x.minQuantity == highlightedMinQuantity);
}
